import { IconButton, Stack, Tooltip } from '@mui/material';
import { FilterKeys } from '@schooly/api';
import {
  GenderTagSelect,
  GroupTagSelect,
  NationalityTagSelect,
  PropertyTypeTagSelect,
  SubjectTagSelect,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { CrossIcon, TagSelect } from '@schooly/style';
import React, { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../hooks/useSchool';
import { AddStudentsFilters, AddStudentsFiltersKey } from './AddStudentsFiltersPopup';

type InlineFiltersTagsProps = {
  filtersAvailable: AddStudentsFiltersKey[];
  onOpenFilters: () => void;
  onResetFilters?: () => void;
  maxAmount?: number;
  filters: AddStudentsFilters;
};

export const InlineFiltersTags: FC<InlineFiltersTagsProps> = ({
  filtersAvailable,
  filters,
  onOpenFilters,
  onResetFilters,
  maxAmount = 2,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId } = useSchool();

  const tags = useMemo(() => {
    const tagComponents: ReactNode[] = [];

    if (!schoolId) return tagComponents;

    const commonTagsProps = {
      size: 'small',
      onClick: onOpenFilters,
      sx: { maxWidth: 115 },
    } as const;

    for (const id of [
      ...(filtersAvailable.includes(FilterKeys.Status) ? filters.status || [] : []),
      ...(filtersAvailable.includes(FilterKeys.AgeGroup) ? filters.age_group || [] : []),
      ...(filtersAvailable.includes(FilterKeys.House) ? filters.house || [] : []),
    ]) {
      tagComponents.push(
        <PropertyTypeTagSelect
          key={id}
          id={id}
          schoolId={schoolId}
          userRole={SchoolUserRole.Student}
          {...commonTagsProps}
        />,
      );
    }

    for (const id of filtersAvailable.includes(FilterKeys.Group) ? filters.group || [] : []) {
      tagComponents.push(<GroupTagSelect key={id} id={id} {...commonTagsProps} />);
    }

    for (const id of filters.subject ?? []) {
      tagComponents.push(
        <SubjectTagSelect schoolId={schoolId} key={id} id={id} {...commonTagsProps} />,
      );
    }

    for (const id of filters.nationality ?? []) {
      tagComponents.push(<NationalityTagSelect key={id} nationality={id} {...commonTagsProps} />);
    }

    for (const id of filters.gender ?? []) {
      tagComponents.push(<GenderTagSelect key={id} gender={id} {...commonTagsProps} />);
    }

    if (tagComponents.length > maxAmount) {
      return [
        ...tagComponents.slice(0, maxAmount),
        <TagSelect
          key={`+${tagComponents.length - maxAmount}`}
          label={`+${tagComponents.length - maxAmount}`}
          {...commonTagsProps}
        />,
      ];
    }

    return tagComponents;
  }, [filtersAvailable, filters, onOpenFilters, schoolId, maxAmount]);

  if (!tags.length) return null;

  return (
    <Stack flexDirection="row" flexWrap="wrap" gap={1}>
      {tags}{' '}
      {onResetFilters && (
        <Tooltip
          title={formatMessage({ id: 'filter-ResetToDefault' })}
          componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
        >
          <IconButton
            onClick={onResetFilters}
            sx={(theme) => ({
              color: theme.palette.common.grey,
              '&:hover': { color: theme.palette.text.primary },
            })}
          >
            <CrossIcon />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};
